import { useEffect, useCallback, useMemo, useRef } from 'react'
import debounce from 'lodash.debounce'
import {
  ViewportDimensions,
  ViewportOrientation,
  ViewportOrientationValues,
} from '@contexts/viewport'

interface useViewportDimensionsProps {
  onSizeChange: (dimensions: ViewportDimensions) => void
  onViewportChange: (dimensions: ViewportDimensions) => void
  onOrientationChange: (orientation: ViewportOrientation) => void
}

const DEBOUNCE_TIMEOUT = 500

const useViewportDimensions = ({
  onSizeChange,
  onViewportChange,
  onOrientationChange,
}: useViewportDimensionsProps) => {
  const orientationMatchFirstRun = useRef(false)
  const windowDimensionsFirstRun = useRef(false)
  const orientationMatch = useRef<MediaQueryList | null>(null)

  const onSizeChangeCallback = useMemo(
    () =>
      debounce(() => {
        onSizeChange({ width: window.innerWidth, height: window.innerHeight })
      }, DEBOUNCE_TIMEOUT),
    [onSizeChange]
  )

  const onViewportChangeCallback = useCallback(() => {
    onViewportChange({
      width: window.innerWidth,
      height: window.innerHeight,
    })
  }, [onViewportChange])

  const onOrientationChangeCallback = useCallback(
    (e: any) => {
      onOrientationChange({
        orientation: e.matches
          ? ViewportOrientationValues.LANDSCAPE
          : ViewportOrientationValues.PORTRAIT,
      })
    },
    [onOrientationChange]
  )

  useEffect(() => {
    if (!orientationMatchFirstRun.current) {
      orientationMatchFirstRun.current = true
      orientationMatch.current = matchMedia('(orientation: landscape)')
      onOrientationChangeCallback(orientationMatch.current)
    }
    if (!windowDimensionsFirstRun.current) {
      windowDimensionsFirstRun.current = true
      onSizeChangeCallback()
      onViewportChangeCallback()
    }
    window.addEventListener('resize', onSizeChangeCallback)
    window.addEventListener('resize', onViewportChangeCallback)
    orientationMatch.current?.addListener(onOrientationChangeCallback)
    return () => {
      window.removeEventListener('resize', onSizeChangeCallback)
      window.removeEventListener('resize', onViewportChangeCallback)
      orientationMatch.current?.removeListener(onOrientationChangeCallback)
    }
  }, [
    onSizeChangeCallback,
    onViewportChangeCallback,
    onOrientationChangeCallback,
  ])
}

export default useViewportDimensions
